import React, { useState } from 'react';
import { X, Mail, Calendar, Bell, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { subscribeToNewsletter } from '@/lib/services/newsletter.service';
import { useToast } from '@/components/ui/shared/toast';

interface NotificationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  date: string;
  onEmailSubmit: (email: string) => void;
}

export function NotificationPopup({ isOpen, onClose, title, date, onEmailSubmit }: NotificationPopupProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { showToast } = useToast();

  // Mevcut handleEmailSubmit fonksiyonunu güncelleyin
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // API çağrısı
      await subscribeToNewsletter(email, title, date);
      setIsSubmitted(true);
      onEmailSubmit(email);

      // Başarılı bildirim
      showToast('success', 'Your subscription has been successfully!');
    } catch (error) {
      // Hata yönetimi
      console.error('Subscription error:', error);

      // Hata bildirimi
      showToast('error', 'Subscription failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddToCalendar = () => {
    // Gelen date string'ini Date objesine çevirelim
    const eventDate = new Date(date);

    // Başlangıç tarihi için format oluşturalım (YYYYMMDDTHHMMSSZ)
    const startDate = eventDate.toISOString().replace(/-|:|\.\d+/g, '');

    // Bitiş tarihi olarak 1 saat sonrasını ayarlayalım
    const endDate = new Date(eventDate.getTime() + 60 * 60 * 1000).toISOString().replace(/-|:|\.\d+/g, '');

    // Google Calendar formatında dates parametresi
    const formattedDates = `${startDate}/${endDate}`;

    // Create calendar event data
    const event = {
      text: title,
      dates: formattedDates,
      details: `Reminder for ${title} drop` + '\n' + 'https://orkhan.art',
      location: 'Online',
    };

    // Create Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.text)}&dates=${encodeURIComponent(event.dates)}&details=${encodeURIComponent(event.details)}&location=${encodeURIComponent(event.location)}`;

    // Open calendar in new tab
    window.open(googleCalendarUrl, '_blank');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-sm shadow-lg z-50 p-6 font-mono"
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-black/70" />
                <h3 className="text-[12px] font-bold uppercase tracking-wider">Get Notified</h3>
              </div>
              <button onClick={onClose} className="text-black/40 hover:text-black/60 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            {!isSubmitted ? (
              <>
                <p className="text-[10px] text-black/60 mb-6 uppercase tracking-wide">
                  Stay updated about the {title} drop. We'll notify you when it's live.
                </p>

                {/* Email Form */}
                <form onSubmit={handleEmailSubmit} className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-3.5 w-3.5 text-black/40" />
                    <label className="text-[8px] uppercase tracking-wider text-black/60">Email Address</label>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 text-[10px] border border-zinc-200 rounded-sm focus:outline-none focus:border-black/20 bg-zinc-50/50"
                    required
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full mt-3 bg-black text-white text-[10px] uppercase font-bold py-2.5 rounded-sm hover:bg-black/80 transition-colors duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Subscribe'}
                  </button>
                </form>

                {/* Calendar Button */}
                <button
                  onClick={handleAddToCalendar}
                  className="w-full flex items-center justify-center gap-2 bg-zinc-50 text-black/70 text-[10px] uppercase font-bold py-2.5 rounded-sm hover:bg-zinc-100 transition-colors"
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Add to Calendar
                </button>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-zinc-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-6 w-6 text-black/70" />
                </div>
                <h4 className="text-[12px] font-bold uppercase tracking-wider mb-2">Successfully Subscribed!</h4>
                <p className="text-[10px] text-black/60 uppercase tracking-wide">
                  You'll receive a notification when the drop is live.
                </p>
                <button
                  onClick={onClose}
                  className="mt-4 text-[10px] text-black/70 hover:text-black/90 uppercase font-bold tracking-wide"
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
